import { useEffect, useRef, useState } from 'react';

import { Modal, toastService } from '@thefarmersfront/kitchen';

import { useStableCallback } from '~/hooks/common';
import {
  useGetOwnerSupplierBusinessNumberExists,
  useGetOwnerSupplierOwnerSupplierCode,
  usePatchOwnerSupplier,
} from '~/hooks/vender';
import { useLoadingStore, useUserInfoStore } from '~/store';
import { focusFirstErrorField, formatDate, validateRepresentativeTelFormat, validateTelFormat } from '~/utils';

import {
  DEFAULT_INPUT_STATUS,
  ERROR_BUSINESS_NUMBER_SEARCH_STATUS,
  ERROR_DUPLICATED_BUSINESS_NUMBER_STATUS,
  ERROR_INVALID_FORMAT_INPUT_STATUS,
  ERROR_REQUIRED_INPUT_STATUS,
  PICKUP_SHIPPING_TYPES,
  TOAST_MESSAGE,
  VENDER_DATE_FORMAT,
} from '~/constants';

import { VenderSupplierDetailModalDefaultInfoSection } from '../VenderSupplierDetailModalDefaultInfoSection';
import { VenderSupplierDetailModalManagerInfoSection } from '../VenderSupplierDetailModalManagerInfoSection';
import { VenderSupplierDetailModalShippingAddressInfoSection } from '../VenderSupplierDetailModalShippingAddressInfoSection';

import type { TypeErrorResponseData } from '~/types/common.types';
import type { TypePickupInfo, TypeSupplierDetailFormStatus, TypeSupplierDetailFormValue } from '~/types/vender.types';

import styles from './VenderSupplierDetailModal.module.scss';

type TypeVenderSupplierDetailModalProps = {
  open: boolean;
  ownerSupplierCode: string;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
};

const DEFAULT_SUPPLIER_DETAIL_FORM_VALUE: TypeSupplierDetailFormValue = {
  ownerOriginSupplierCode: '',
  businessNumber: '',
  supplierName: '',
  businessConditions: '',
  businessCategory: '',
  ceoName: '',
  zipCode: '',
  address: '',
  detailAddress: '',
  tel: '',
  fax: '',
  dealStartDate: null,
  dealEndDate: null,
  isUseStatus: true,
  isUsePickup: false,
  isUseKurlyPickup: false,
  isUseMilkRunPickup: false,
  kurlyPickupInfo: {
    zipCode: '',
    address: '',
    detailAddress: '',
    contactNumber: '',
  },
  milkRunPickupInfo: {
    zipCode: '',
    address: '',
    detailAddress: '',
    contactNumber: '',
  },
  managerInfoList: [],
};

const DEFAULT_SUPPLIER_DETAIL_FORM_STATUS = {
  businessNumber: DEFAULT_INPUT_STATUS,
  supplierName: DEFAULT_INPUT_STATUS,
  tel: DEFAULT_INPUT_STATUS,
  fax: DEFAULT_INPUT_STATUS,
  pickupType: DEFAULT_INPUT_STATUS,
  kurlyPickupZipCode: DEFAULT_INPUT_STATUS,
  kurlyPickupAddress: DEFAULT_INPUT_STATUS,
  kurlyPickupContactNumber: DEFAULT_INPUT_STATUS,
  milkRunPickupZipCode: DEFAULT_INPUT_STATUS,
  milkRunPickupAddress: DEFAULT_INPUT_STATUS,
  milkRunPickupContactNumber: DEFAULT_INPUT_STATUS,
};

const DEFAULT_SUPPLIER_DETAIL_ERROR_FIELD_REFS: TypeVenderSupplierDetailErrorFieldRefs = {
  businessNumber: null,
  supplierName: null,
  tel: null,
  fax: null,
  pickupType: null,
  kurlyPickupZipCode: null,
  kurlyPickupAddress: null,
  kurlyPickupContactNumber: null,
  milkRunPickupZipCode: null,
  milkRunPickupAddress: null,
  milkRunPickupContactNumber: null,
};

export type TypeVenderSupplierDetailErrorFieldRefs = {
  businessNumber: HTMLInputElement | null;
  supplierName: HTMLInputElement | null;
  tel: HTMLInputElement | null;
  fax: HTMLInputElement | null;
  pickupType: HTMLDivElement | null;
  kurlyPickupZipCode: HTMLInputElement | null;
  kurlyPickupAddress: HTMLInputElement | null;
  kurlyPickupContactNumber: HTMLInputElement | null;
  milkRunPickupZipCode: HTMLInputElement | null;
  milkRunPickupAddress: HTMLInputElement | null;
  milkRunPickupContactNumber: HTMLInputElement | null;
};

/**
 * [화주사정보관리 > 공급사관리] 공급사 상세 모달 컴포넌트
 */
const VenderSupplierDetailModal = ({
  open,
  ownerSupplierCode,
  onCancel,
  onConfirm,
}: TypeVenderSupplierDetailModalProps) => {
  const { setIsLoading } = useLoadingStore();
  const { userInfo } = useUserInfoStore();

  const [venderSupplierDetailForm, setVenderSupplierDetailForm] = useState<TypeSupplierDetailFormValue>(
    DEFAULT_SUPPLIER_DETAIL_FORM_VALUE
  );
  const [initialBusinessNumber, setInitialBusinessNumber] = useState(''); // 변경 전 사업자번호

  const [venderSupplierDetailFormStatus, setVenderSupplierDetailFormStatus] = useState<TypeSupplierDetailFormStatus>(
    DEFAULT_SUPPLIER_DETAIL_FORM_STATUS
  );

  // 사업자번호 유효성 or 중복 체크 여부 (boolean:체크 후 결과, null:체크 전)
  const [isBusinessNumberValid, setIsBusinessNumberValid] = useState<boolean | null>(null);
  const [isBusinessNumberExist, setIsBusinessNumberExist] = useState<boolean | null>(null);

  const errorFieldRefs = useRef<TypeVenderSupplierDetailErrorFieldRefs>(DEFAULT_SUPPLIER_DETAIL_ERROR_FIELD_REFS);

  const { getOwnerSupplierOwnerSupplierCode } = useGetOwnerSupplierOwnerSupplierCode();
  const { getOwnerSupplierBusinessNumberExists } = useGetOwnerSupplierBusinessNumberExists();
  const { patchOwnerSupplier } = usePatchOwnerSupplier();

  const fetchSupplierInfo = useStableCallback(async () => {
    try {
      setIsLoading(true);
      const data = await getOwnerSupplierOwnerSupplierCode({ ownerSupplierCode });

      setVenderSupplierDetailForm({
        ...venderSupplierDetailForm,
        ownerOriginSupplierCode: data.ownerOriginSupplierCode ?? '',
        businessNumber: data.businessNumber,
        supplierName: data.ownerSupplierName,
        businessConditions: data.businessConditions,
        businessCategory: data.businessCategory,
        ceoName: data.ceoName,
        zipCode: data.zipCode,
        address: data.address,
        detailAddress: data.detailAddress,
        tel: data.ownerSupplierTel,
        fax: data.ownerSupplierFax,
        dealStartDate: data.dealStartDate ? new Date(data.dealStartDate) : null,
        dealEndDate: data.dealEndDate ? new Date(data.dealEndDate) : null,
        isUseStatus: data.useStatus === 'Y',
        isUsePickup: data.ownerSupplierShippingAddressDetailList.length > 0,
        isUseKurlyPickup: data.useKurlyPickup,
        isUseMilkRunPickup: data.useMilkRunPickup,
        kurlyPickupInfo: (() => {
          const kurlyPickupAddress = data.ownerSupplierShippingAddressDetailList.find(
            (shippingAddress) => shippingAddress.shippingType === PICKUP_SHIPPING_TYPES.KURLY_PICKUP
          );
          if (!kurlyPickupAddress) return DEFAULT_SUPPLIER_DETAIL_FORM_VALUE.kurlyPickupInfo;

          const { shippingType: _shippingType, ...rest } = kurlyPickupAddress;
          return rest;
        })(),
        milkRunPickupInfo: (() => {
          const milkRunPickupAddress = data.ownerSupplierShippingAddressDetailList.find(
            (shippingAddress) => shippingAddress.shippingType === PICKUP_SHIPPING_TYPES.MILKRUN_PICKUP
          );
          if (!milkRunPickupAddress) return DEFAULT_SUPPLIER_DETAIL_FORM_VALUE.milkRunPickupInfo;

          const { shippingType: _shippingType, ...rest } = milkRunPickupAddress;
          return rest;
        })(),
        managerInfoList: data.ownerSupplierManagerList,
      });

      setInitialBusinessNumber(data.businessNumber);
      setIsBusinessNumberValid(data.businessNumberChecked);
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  });

  useEffect(() => {
    if (open) {
      fetchSupplierInfo();
    }
  }, [open, fetchSupplierInfo]);

  const fetchBusinessNumberExist = async () => {
    try {
      setIsLoading(true);

      const isExist = await getOwnerSupplierBusinessNumberExists({
        businessNumber: venderSupplierDetailForm.businessNumber,
      });

      setIsBusinessNumberExist(isExist);

      return isExist;
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const createPickupShippingAddresses = (
    usePickup: boolean,
    useKurlyPickup: boolean,
    useMilkRunPickup: boolean,
    kurlyPickupInfo: TypePickupInfo,
    milkRunPickupInfo: TypePickupInfo
  ) => {
    if (!usePickup) return [];

    const addresses = [];

    if (useKurlyPickup) {
      addresses.push({
        shippingType: PICKUP_SHIPPING_TYPES.KURLY_PICKUP,
        zipCode: kurlyPickupInfo.zipCode,
        address: kurlyPickupInfo.address,
        detailAddress: kurlyPickupInfo.detailAddress,
        contactNumber: kurlyPickupInfo.contactNumber,
      });
    }
    if (useMilkRunPickup) {
      addresses.push({
        shippingType: PICKUP_SHIPPING_TYPES.MILKRUN_PICKUP,
        zipCode: milkRunPickupInfo.zipCode,
        address: milkRunPickupInfo.address,
        detailAddress: milkRunPickupInfo.detailAddress,
        contactNumber: milkRunPickupInfo.contactNumber,
      });
    }

    return addresses;
  };

  const updateOwnerSupplier = async () => {
    try {
      setIsLoading(true);

      const {
        ownerOriginSupplierCode,
        businessNumber,
        supplierName,
        businessConditions,
        businessCategory,
        ceoName,
        zipCode,
        address,
        detailAddress,
        tel,
        fax,
        dealStartDate,
        dealEndDate,
        isUseStatus,
        isUsePickup,
        isUseKurlyPickup,
        isUseMilkRunPickup,
        kurlyPickupInfo,
        milkRunPickupInfo,
      } = venderSupplierDetailForm;

      const params = {
        ownerCode: userInfo.ownerCode,
        ownerSupplierCode,
        ownerOriginSupplierCode,
        businessNumber,
        businessNumberChecked: isBusinessNumberValid === true,
        ownerSupplierName: supplierName,
        businessConditions,
        businessCategory,
        ceoName,
        zipCode,
        address,
        detailAddress,
        ownerSupplierTel: tel,
        ownerSupplierFax: fax,
        dealStartDate: formatDate(dealStartDate, VENDER_DATE_FORMAT.DATE_TIME),
        dealEndDate: formatDate(dealEndDate, VENDER_DATE_FORMAT.DATE_TIME),
        useStatus: isUseStatus ? 'Y' : 'N',
        useKurlyPickup: isUsePickup && isUseKurlyPickup,
        useMilkRunPickup: isUsePickup && isUseMilkRunPickup,
        ownerSupplierShippingAddressRequests: createPickupShippingAddresses(
          isUsePickup,
          isUseKurlyPickup,
          isUseMilkRunPickup,
          kurlyPickupInfo,
          milkRunPickupInfo
        ),
      };

      await patchOwnerSupplier(params);

      await onConfirm();
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const validateBusinessNumber = async (
    formValue: TypeSupplierDetailFormValue,
    formStatus: TypeSupplierDetailFormStatus
  ): Promise<boolean> => {
    if (formValue.businessNumber.length === 0) {
      formStatus.businessNumber = ERROR_REQUIRED_INPUT_STATUS;
    } else if (isBusinessNumberValid === null) {
      formStatus.businessNumber = ERROR_BUSINESS_NUMBER_SEARCH_STATUS;
    } else if (isBusinessNumberExist === null && initialBusinessNumber !== formValue.businessNumber) {
      const isExist = await fetchBusinessNumberExist();

      if (isExist) {
        formStatus.businessNumber = ERROR_DUPLICATED_BUSINESS_NUMBER_STATUS;
      }
    } else if (isBusinessNumberExist === true) {
      formStatus.businessNumber = ERROR_DUPLICATED_BUSINESS_NUMBER_STATUS;
    }

    const isValid = formStatus.businessNumber.status !== 'error';

    return isValid;
  };

  const validateBasicInfo = (
    formValue: TypeSupplierDetailFormValue,
    formStatus: TypeSupplierDetailFormStatus
  ): boolean => {
    if (formValue.supplierName.trim() === '') {
      formStatus.supplierName = ERROR_REQUIRED_INPUT_STATUS;
    }

    if (formValue.tel.length > 0 && !validateRepresentativeTelFormat(formValue.tel)) {
      formStatus.tel = ERROR_INVALID_FORMAT_INPUT_STATUS;
    }

    if (formValue.fax.length > 0 && !validateTelFormat(formValue.fax)) {
      formStatus.fax = ERROR_INVALID_FORMAT_INPUT_STATUS;
    }

    const isValid = !(
      formStatus.supplierName.status === 'error' ||
      formStatus.tel.status === 'error' ||
      formStatus.fax.status === 'error'
    );

    return isValid;
  };

  const validatePickupInfo = (
    formValue: TypeSupplierDetailFormValue,
    formStatus: TypeSupplierDetailFormStatus
  ): boolean => {
    let isValid = true;

    if (formValue.isUsePickup && !formValue.isUseKurlyPickup && !formValue.isUseMilkRunPickup) {
      formStatus.pickupType = ERROR_REQUIRED_INPUT_STATUS;
      isValid = false;
    }

    if (formValue.isUsePickup && formValue.isUseKurlyPickup) {
      const { zipCode, address, contactNumber } = formValue.kurlyPickupInfo;

      if (zipCode.length === 0) {
        formStatus.kurlyPickupZipCode = ERROR_REQUIRED_INPUT_STATUS;
      }

      if (address.length === 0) {
        formStatus.kurlyPickupAddress = ERROR_REQUIRED_INPUT_STATUS;
      }

      if (contactNumber.length === 0) {
        formStatus.kurlyPickupContactNumber = ERROR_REQUIRED_INPUT_STATUS;
      } else if (!validateTelFormat(contactNumber)) {
        formStatus.kurlyPickupContactNumber = ERROR_INVALID_FORMAT_INPUT_STATUS;
      }

      isValid =
        isValid &&
        !(
          formStatus.kurlyPickupZipCode.status === 'error' ||
          formStatus.kurlyPickupAddress.status === 'error' ||
          formStatus.kurlyPickupContactNumber.status === 'error'
        );
    }

    if (formValue.isUsePickup && formValue.isUseMilkRunPickup) {
      const { zipCode, address, contactNumber } = formValue.milkRunPickupInfo;
      formStatus.milkRunPickupZipCode =
        zipCode.length === 0 ? ERROR_REQUIRED_INPUT_STATUS : formStatus.milkRunPickupZipCode;
      formStatus.milkRunPickupAddress =
        address.length === 0 ? ERROR_REQUIRED_INPUT_STATUS : formStatus.milkRunPickupAddress;
      formStatus.milkRunPickupContactNumber =
        contactNumber.length === 0 ? ERROR_REQUIRED_INPUT_STATUS : formStatus.milkRunPickupContactNumber;
      formStatus.milkRunPickupContactNumber =
        contactNumber.length > 0 && !validateTelFormat(contactNumber)
          ? ERROR_INVALID_FORMAT_INPUT_STATUS
          : formStatus.milkRunPickupContactNumber;

      isValid =
        isValid &&
        !(
          formStatus.milkRunPickupZipCode.status === 'error' ||
          formStatus.milkRunPickupAddress.status === 'error' ||
          formStatus.milkRunPickupContactNumber.status === 'error'
        );
    }

    return isValid;
  };

  const validateForm = async () => {
    const updatedFormStatus = { ...venderSupplierDetailFormStatus };

    // 사업자번호 검증
    const isBusinessNumberValid = await validateBusinessNumber(venderSupplierDetailForm, updatedFormStatus);
    // 기본 정보 검증
    const isBasicInfoValid = validateBasicInfo(venderSupplierDetailForm, updatedFormStatus);
    // 출고지 정보 검증
    const isPickupInfoValid = validatePickupInfo(venderSupplierDetailForm, updatedFormStatus);

    setVenderSupplierDetailFormStatus(updatedFormStatus);

    return {
      isValid: isBusinessNumberValid && isBasicInfoValid && isPickupInfoValid,
      updatedFormStatus,
    };
  };

  const handleConfirm = async () => {
    const { isValid: isValidForm, updatedFormStatus } = await validateForm();

    if (isValidForm) {
      await updateOwnerSupplier();
    } else {
      focusFirstErrorField(errorFieldRefs.current, updatedFormStatus);
    }
  };

  return (
    <Modal
      title="공급사 상세"
      open={open}
      size="xlarge"
      confirmText="저장"
      confirmButtonType="submit"
      onCancel={onCancel}
      onConfirm={handleConfirm}
    >
      <div className={styles['modal-contents-container']}>
        <VenderSupplierDetailModalDefaultInfoSection
          errorFieldRefs={errorFieldRefs}
          ownerSupplierCode={ownerSupplierCode}
          venderSupplierDetailForm={venderSupplierDetailForm}
          venderSupplierDetailFormStatus={venderSupplierDetailFormStatus}
          isBusinessNumberValid={isBusinessNumberValid}
          setVenderSupplierDetailForm={setVenderSupplierDetailForm}
          setVenderSupplierDetailFormStatus={setVenderSupplierDetailFormStatus}
          setIsBusinessNumberValid={setIsBusinessNumberValid}
          setIsBusinessNumberExist={setIsBusinessNumberExist}
        />
        <VenderSupplierDetailModalShippingAddressInfoSection
          errorFieldRefs={errorFieldRefs}
          venderSupplierDetailForm={venderSupplierDetailForm}
          venderSupplierDetailFormStatus={venderSupplierDetailFormStatus}
          setVenderSupplierDetailForm={setVenderSupplierDetailForm}
          setVenderSupplierDetailFormStatus={setVenderSupplierDetailFormStatus}
        />
        <VenderSupplierDetailModalManagerInfoSection
          ownerCode={userInfo.ownerCode}
          ownerSupplierCode={ownerSupplierCode}
          venderSupplierDetailForm={venderSupplierDetailForm}
          setVenderSupplierDetailForm={setVenderSupplierDetailForm}
        />
      </div>
    </Modal>
  );
};

export default VenderSupplierDetailModal;
